import { Button, Select, type SelectOption } from "@rio-cloud/rio-uikit";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { SupportType } from "../constants";
import { getSupportSubjectOptions, getSupportThemeOptions } from "../utils";
import DateRangeFilter from "./DateRangeFilter";
import SidebarSheet from "./SidebarSheet";

export interface SupportFilters {
    responsible: string;
    regional: string;
    subject: string;
    subtopic: string;
    startDate: string;
    endDate: string;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    responsible: string;
    regional: string;
    subject: string;
    subtopic: string;
    startDate: string;
    endDate: string;
    supportType: string;
    optionsRegionals: SelectOption[];
    onApply: (filters: SupportFilters) => void;
}

const SettingsSidebar = ({
    isOpen,
    onClose,
    responsible,
    regional,
    subject,
    subtopic,
    startDate,
    endDate,
    supportType,
    optionsRegionals,
    onApply,
}: Props) => {
    const intl = useIntl();
    const [draftResponsible, setDraftResponsible] = useState(responsible);
    const [draftRegional, setDraftRegional] = useState(regional);
    const [draftSubject, setDraftSubject] = useState(subject);
    const [draftSubtopic, setDraftSubtopic] = useState(subtopic);
    const [draftStartDate, setDraftStartDate] = useState(startDate);
    const [draftEndDate, setDraftEndDate] = useState(endDate);

    useEffect(() => {
        if (isOpen) {
            setDraftResponsible(responsible);
            setDraftRegional(regional);
            setDraftSubject(subject);
            setDraftSubtopic(subject === "all" ? "" : subtopic);
            setDraftStartDate(startDate);
            setDraftEndDate(endDate);
        }
    }, [isOpen, responsible, regional, subject, subtopic, startDate, endDate]);

    const handleApply = () => {
        onApply({
            responsible: draftResponsible,
            regional: draftRegional,
            subject: draftSubject,
            subtopic: draftSubtopic,
            startDate: draftStartDate,
            endDate: draftEndDate,
        });
        onClose();
    };

    const handleClear = () => {
        setDraftResponsible("");
        setDraftRegional("all");
        setDraftSubject("all");
        setDraftSubtopic("");
        setDraftStartDate("");
        setDraftEndDate("");
        onApply({
            responsible: "",
            regional: "all",
            subject: "all",
            subtopic: "",
            startDate: "",
            endDate: "",
        });
    };

    return (
        <SidebarSheet
            isOpen={isOpen}
            onClose={onClose}
            title={intl.formatMessage({ id: "intl-msg:tas.support.filters.title" })}
            footer={(
                <>
                    <Button onClick={handleClear}>
                        {intl.formatMessage({ id: "intl-msg:tas.support.filters.clear" })}
                    </Button>
                    <Button bsStyle={Button.PRIMARY} onClick={handleApply}>
                        {intl.formatMessage({ id: "intl-msg:tas.support.filters.apply" })}
                    </Button>
                </>
            )}
        >
            <div className="form-group">
                <label htmlFor="settings-regional">
                    {intl.formatMessage({ id: "intl-msg:tas.support.toolbar.regional" })}
                </label>
                <Select
                    id="settings-regional"
                    placeholder={intl.formatMessage({ id: "intl-msg:tas.support.toolbar.selectPlaceholder" })}
                    value={[draftRegional]}
                    options={optionsRegionals}
                    useFilter
                    className="border"
                    noItemMessage={intl.formatMessage({ id: "intl-msg:tas.support.toolbar.noRegionalFound" })}
                    onChange={(selected) => {
                        if (selected?.id) setDraftRegional(selected.id);
                    }}
                />
            </div>

            <div className="form-group">
                <label htmlFor="settings-responsible">
                    {intl.formatMessage({ id: "intl-msg:tas.support.toolbar.responsible" })}
                </label>
                <input
                    type="text"
                    className="form-control"
                    id="settings-responsible"
                    value={draftResponsible}
                    onChange={(event) => setDraftResponsible(event.target.value)}
                    maxLength={17}
                />
            </div>

            <div className="form-group">
                <label htmlFor="settings-subject">
                    {intl.formatMessage({ id: "intl-msg:tas.support.filters.subject" })}
                </label>
                <Select
                    id="settings-subject"
                    placeholder={intl.formatMessage({ id: "intl-msg:tas.support.filters.selectSubject" })}
                    value={draftSubject ? [draftSubject] : []}
                    options={getSupportSubjectOptions(intl, supportType, {
                        id: "all",
                        label: intl.formatMessage({ id: "intl-msg:tas.support.toolbar.supportType.all" }),
                    })}
                    useFilter
                    className="border"
                    noItemMessage={intl.formatMessage({ id: "intl-msg:tas.support.toolbar.noSubjectFound" })}
                    onChange={(selected) => {
                        setDraftSubject(selected?.id || "");
                        setDraftSubtopic("");
                    }}
                />
            </div>

            {draftSubject !== SupportType.COMMERCIAL &&
                draftSubject !== SupportType.CONTRACT_CANCELLATION &&
                supportType !== "TECHNICAL_SUPPORT" ? (
                <div className="form-group">
                    <label htmlFor="settings-subtopic">
                        {intl.formatMessage({ id: "intl-msg:tas.support.filters.theme" })}
                    </label>
                    <Select
                        id="settings-subtopic"
                        placeholder={intl.formatMessage({ id: "intl-msg:tas.support.filters.selectTheme" })}
                        value={draftSubtopic ? [draftSubtopic] : []}
                        options={getSupportThemeOptions(intl, draftSubject)}
                        disabled={draftSubject === "all"}
                        useFilter
                        className="border"
                        onChange={(selected) => setDraftSubtopic(selected?.id || "")}
                    />
                </div>
            ) : null}

            <DateRangeFilter
                title={intl.formatMessage({ id: "intl-msg:tas.support.filters.openingDate" })}
                startDate={draftStartDate || undefined}
                endDate={draftEndDate || undefined}
                onChange={({ startDate: nextStartDate, endDate: nextEndDate }) => {
                    setDraftStartDate(nextStartDate || "");
                    setDraftEndDate(nextEndDate || "");
                }}
            />
        </SidebarSheet>
    );
};

export default SettingsSidebar;
